"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import Box from "@mui/material/Box";
// Importing component short-name index into debug overlay
import { getShortComponentLabel } from "@/components/debug/componentNameIndex";
import type { DebugMode } from "@/components/debug/DebugModeContext";

type DebugOverlayProps = {
  mode: DebugMode;
};

type FiberNode = {
  type?: unknown;
  return?: FiberNode | null;
  memoizedProps?: unknown;
};

type InspectorState = {
  x: number;
  y: number;
  viewportWidth: number;
  viewportHeight: number;
  name: string;
  type: string;
  action: string;
  visible: boolean;
};

function getReactFiberNode(element: HTMLElement): FiberNode | null {
  const elementWithInternal = element as unknown as Record<string, unknown>;
  for (const key of Object.keys(elementWithInternal)) {
    if (key.startsWith("__reactFiber$") || key.startsWith("__reactContainer$")) {
      const node = elementWithInternal[key];
      if (node && typeof node === "object") {
        return node as FiberNode;
      }
    }
  }

  return null;
}

function getFiberComponentName(typeValue: unknown): string | null {
  if (typeof typeValue === "function") {
    const candidate =
      (typeValue as { displayName?: string }).displayName ||
      (typeValue as { name?: string }).name ||
      null;
    return candidate && candidate.trim().length > 0 ? candidate : null;
  }

  if (typeof typeValue === "object" && typeValue) {
    const typeObject = typeValue as {
      displayName?: string;
      render?: { displayName?: string; name?: string };
      type?: { displayName?: string; name?: string };
    };

    const fromDisplayName = typeObject.displayName?.trim();
    if (fromDisplayName) {
      return fromDisplayName;
    }

    const fromRender =
      typeObject.render?.displayName?.trim() ||
      typeObject.render?.name?.trim() ||
      null;
    if (fromRender) {
      return fromRender;
    }

    const fromType =
      typeObject.type?.displayName?.trim() ||
      typeObject.type?.name?.trim() ||
      null;
    if (fromType) {
      return fromType;
    }
  }

  return null;
}

function isUsefulComponentName(name: string): boolean {
  if (!name) {
    return false;
  }

  const trimmed = name.trim();
  // Production minification often collapses component names to short tokens like "e", "I", "Y".
  // Those are not meaningful for UI inspection, so we hide them.
  if (/^[A-Za-z]$/.test(trimmed)) {
    return false;
  }

  if (/^[A-Za-z]\d?$/.test(trimmed) && trimmed.length <= 2) {
    return false;
  }

  if (name.startsWith("Mui")) {
    return false;
  }

  if (
    name.startsWith("Styled(") ||
    name === "LinkComponent" ||
    name === "Unknown" ||
    name === "ForwardRef"
  ) {
    return false;
  }

  const genericNames = new Set([
    "Box",
    "Grid",
    "Stack",
    "Card",
    "Paper",
    "Container",
    "Typography",
    "Button",
    "IconButton",
    "Link",
    "Avatar",
    "Table",
    "TableRow",
    "TableCell",
    "TableBody",
    "TableHead",
    "List",
    "ListItem",
    "Divider",
    "Dialog",
    "Menu",
    "MenuItem",
    "Select",
    "TextField",
    "Checkbox",
    "Switch",
    "Slider",
    "Chip",
    "Tooltip",
    "SvgIcon",
  ]);

  if (genericNames.has(name)) {
    return false;
  }

  return true;
}

function normalizeActionName(name: string): string | null {
  const normalized = name.replace(/^bound\s+/, "").trim();
  if (!normalized) {
    return null;
  }

  const ignoredNames = new Set([
    "onClick",
    "onMouseEnter",
    "onMouseLeave",
    "onTouchStart",
    "onFocus",
    "onBlur",
    "handleClick",
    "handleChange",
    "handleClose",
    "handleOpen",
  ]);

  if (ignoredNames.has(normalized)) {
    return null;
  }

  if (normalized.includes(".") || normalized.startsWith("use")) {
    return null;
  }

  return normalized;
}

function getActionNamesFromProps(props: unknown): string[] {
  if (!props || typeof props !== "object") {
    return [];
  }

  const names = new Set<string>();
  for (const [key, value] of Object.entries(props as Record<string, unknown>)) {
    if (typeof value !== "function") {
      continue;
    }

    const isActionLike =
      key === "action" ||
      key.toLowerCase().includes("action") ||
      key.startsWith("on");

    if (!isActionLike) {
      continue;
    }

    const functionName =
      (value as { name?: string }).name?.replace(/^bound\s+/, "").trim() ||
      null;

    if (functionName && functionName !== "anonymous") {
      names.add(functionName);
    }
  }

  return [...names];
}

function getBriefText(value: string | null | undefined): string {
  const normalized = (value ?? "").replace(/\s+/g, " ").trim();
  return normalized;
}

function toTitleCase(value: string): string {
  if (!value) {
    return value;
  }
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function getElementSemanticLabel(target: HTMLElement): string | null {
  let current: HTMLElement | null = target;
  let depth = 0;

  while (current && current !== document.body && depth < 8) {
    const explicitName = current.dataset.debugName?.trim();
    if (explicitName) {
      return explicitName;
    }

    const tagName = current.tagName.toLowerCase();
    const ariaLabel = getBriefText(current.getAttribute("aria-label"));
    const text = getBriefText(current.textContent);

    if (tagName === "a") {
      if (ariaLabel) {
        return `${ariaLabel} link`;
      }
      if (text) {
        return `${text} link`;
      }
      return "Link";
    }

    if (tagName === "button") {
      if (ariaLabel) {
        return `${ariaLabel} button`;
      }
      if (text) {
        return `${text} button`;
      }
      return "Button";
    }

    if (["h1", "h2", "h3", "h4", "h5", "h6"].includes(tagName)) {
      if (text) {
        return `${text} heading`;
      }
      return "Heading";
    }

    if (tagName === "input" || tagName === "textarea" || tagName === "select") {
      const placeholder = getBriefText(current.getAttribute("placeholder"));
      if (ariaLabel) {
        return `${ariaLabel} field`;
      }
      if (placeholder) {
        return `${placeholder} field`;
      }
      return "Field";
    }

    if (tagName === "table") {
      return "Table";
    }
    if (tagName === "th") {
      return text ? `${text} column` : "Column";
    }
    if (tagName === "tr") {
      return "Row";
    }

    current = current.parentElement;
    depth += 1;
  }

  return null;
}

function getElementTypeLabel(target: HTMLElement): string {
  const tagName = target.tagName.toLowerCase();

  if (tagName === "a") {
    return "link";
  }
  if (tagName === "button") {
    return "button";
  }
  if (tagName === "input" || tagName === "textarea" || tagName === "select") {
    return "field";
  }
  if (["h1", "h2", "h3", "h4", "h5", "h6"].includes(tagName)) {
    return "heading";
  }
  if (tagName === "img" || tagName === "svg") {
    return "icon";
  }
  if (tagName === "table") {
    return "table";
  }
  if (tagName === "th") {
    return "column";
  }
  if (tagName === "tr") {
    return "row";
  }

  const role = target.getAttribute("role")?.toLowerCase();
  if (role === "button") {
    return "button";
  }
  if (role === "menu") {
    return "menu";
  }
  if (role === "dialog") {
    return "dialog";
  }
  if (role === "list") {
    return "list";
  }

  return "container";
}

function resolveNameLabel(target: HTMLElement): string {
  const semantic = getElementSemanticLabel(target);
  if (semantic) {
    return toTitleCase(semantic);
  }

  const component = resolveComponentLabel(target);
  if (component) {
    return toTitleCase(component);
  }

  return "Element";
}

function resolveComponentLabel(target: HTMLElement): string | null {
  let current: HTMLElement | null = target;

  while (current && current !== document.body) {
    const explicitComponent = current.dataset.debugComponent?.trim() ?? "";
    if (explicitComponent) {
      return getShortComponentLabel(explicitComponent);
    }

    let fiber = getReactFiberNode(current);
    while (fiber) {
      const name = getFiberComponentName(fiber.type);
      if (name && isUsefulComponentName(name)) {
        return getShortComponentLabel(name);
      }
      fiber = fiber.return ?? null;
    }

    current = current.parentElement;
  }

  return null;
}

function collectActionNamesFromElement(target: HTMLElement): string[] {
  const names = new Set<string>();

  let current: HTMLElement | null = target;
  let domDepth = 0;
  while (current && current !== document.body && domDepth < 8) {
    const explicitActions = (current.dataset.debugAction ?? "")
      .split(",")
      .map((value) => value.trim())
      .filter((value) => value.length > 0);

    for (const actionName of explicitActions) {
      const normalized = normalizeActionName(actionName);
      if (normalized) {
        names.add(normalized);
      }
    }

    let fiber = getReactFiberNode(current);
    let fiberDepth = 0;
    while (fiber && fiberDepth < 3) {
      for (const actionName of getActionNamesFromProps(fiber.memoizedProps)) {
        const normalized = normalizeActionName(actionName);
        if (normalized) {
          names.add(normalized);
        }
      }
      fiber = fiber.return ?? null;
      fiberDepth += 1;
    }

    current = current.parentElement;
    domDepth += 1;
  }

  return [...names];
}

function buildInspectorDetails(mode: DebugMode, target: HTMLElement): {
  name: string;
  type: string;
  action: string;
} {
  void mode;
  const name = resolveNameLabel(target);
  const type = getElementTypeLabel(target);

  const actionNames = collectActionNamesFromElement(target);
  return {
    name,
    type,
    action: actionNames.length > 0 ? actionNames.join(", ") : "view",
  };
}

function toCursorInspectorState(
  mode: DebugMode,
  x: number,
  y: number,
): InspectorState {
  const target = document.elementFromPoint(x, y) as HTMLElement | null;

  if (!target) {
    return {
      x,
      y,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      name: "",
      type: "",
      action: "",
      visible: false,
    };
  }

  if (
    target.dataset.debugOverlayRoot === "true" ||
    target.closest("[data-debug-overlay-root='true']")
  ) {
    return {
      x,
      y,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      name: "",
      type: "",
      action: "",
      visible: false,
    };
  }

  const details = buildInspectorDetails(mode, target);
  if (!details.name.trim()) {
    return {
      x,
      y,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      name: "",
      type: "",
      action: "",
      visible: false,
    };
  }

  return {
    x,
    y,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    name: details.name,
    type: details.type,
    action: details.action,
    visible: true,
  };
}

export default function DebugOverlay({ mode }: DebugOverlayProps) {
  const [state, setState] = React.useState<InspectorState>({
    x: 0,
    y: 0,
    viewportWidth: 0,
    viewportHeight: 0,
    name: "",
    type: "",
    action: "",
    visible: false,
  });
  const labelRef = React.useRef<HTMLDivElement | null>(null);
  const [labelSize, setLabelSize] = React.useState({ width: 320, height: 40 });

  React.useLayoutEffect(() => {
    const node = labelRef.current;
    if (!node) {
      return;
    }

    const rect = node.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      return;
    }

    setLabelSize((previous) => {
      const width = Math.ceil(rect.width);
      const height = Math.ceil(rect.height);
      if (previous.width === width && previous.height === height) {
        return previous;
      }
      return { width, height };
    });
  }, [state.name, state.type, state.action, state.visible]);

  React.useEffect(() => {
    if (mode === "off") {
      setState((previous) => ({
        ...previous,
        visible: false,
        name: "",
        type: "",
        action: "",
      }));
      return;
    }

    let rafId: number | null = null;
    let latestX = 0;
    let latestY = 0;
    let pointerInside = false;

    const refresh = () => {
      rafId = null;
      if (!pointerInside) {
        setState((previous) => ({
          ...previous,
          visible: false,
          name: "",
          type: "",
          action: "",
        }));
        return;
      }

      setState(toCursorInspectorState(mode, latestX, latestY));
    };

    const scheduleRefresh = () => {
      if (rafId !== null) {
        return;
      }
      rafId = window.requestAnimationFrame(refresh);
    };

    const onMove = (event: PointerEvent) => {
      latestX = event.clientX;
      latestY = event.clientY;
      pointerInside =
        latestX >= 0 &&
        latestY >= 0 &&
        latestX <= window.innerWidth &&
        latestY <= window.innerHeight;
      scheduleRefresh();
    };

    const onLeave = () => {
      pointerInside = false;
      scheduleRefresh();
    };

    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerleave", onLeave);
    window.addEventListener("blur", onLeave);

    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerleave", onLeave);
      window.removeEventListener("blur", onLeave);
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [mode]);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const syncViewport = () => {
      setState((previous) => ({
        ...previous,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
      }));
    };

    syncViewport();
    window.addEventListener("resize", syncViewport);

    return () => {
      window.removeEventListener("resize", syncViewport);
    };
  }, []);

  if (mode === "off" || !state.visible || !state.name) {
    return null;
  }

  const xOffset = 14;
  const yOffset = 16;
  const edgeGap = 8;
  const viewportWidth = Math.max(0, state.viewportWidth);
  const viewportHeight = Math.max(0, state.viewportHeight);
  const fullText = `${state.name}${state.type}${state.action}`;
  const maxLabelWidth = Math.max(160, viewportWidth - edgeGap * 2);
  const estimatedLabelWidth = Math.max(240, Math.min(maxLabelWidth, fullText.length * 7));
  const effectiveLabelWidth = Math.min(labelSize.width || estimatedLabelWidth, maxLabelWidth);

  const preferredLeft = state.x + xOffset;
  const flippedLeft = state.x - xOffset - effectiveLabelWidth;
  const rawLeft =
    preferredLeft + effectiveLabelWidth > viewportWidth - edgeGap
      ? flippedLeft
      : preferredLeft;
  const left =
    viewportWidth > 0
      ? Math.min(
          Math.max(edgeGap, rawLeft),
          Math.max(edgeGap, viewportWidth - edgeGap - effectiveLabelWidth),
        )
      : Math.max(edgeGap, rawLeft);

  const preferredTop = state.y + yOffset;
  const flippedTop = state.y - yOffset - labelSize.height;
  const rawTop =
    preferredTop + labelSize.height > viewportHeight - edgeGap
      ? flippedTop
      : preferredTop;
  const top =
    viewportHeight > 0
      ? Math.min(
          Math.max(edgeGap, rawTop),
          Math.max(edgeGap, viewportHeight - edgeGap - labelSize.height),
        )
      : Math.max(edgeGap, rawTop);

  return createPortal(
    <Box
      data-debug-overlay-root="true"
      sx={{
        position: "fixed",
        inset: 0,
        pointerEvents: "none",
        zIndex: 2000,
      }}
    >
      <Box
        ref={labelRef}
        sx={{
          position: "fixed",
          top,
          left,
          width: "max-content",
          maxWidth: `${maxLabelWidth}px`,
          px: 0.75,
          py: 0.35,
          color: "#fff",
          backgroundColor: "rgba(15, 23, 42, 0.9)",
          border: "1px solid rgba(148, 163, 184, 0.42)",
          borderRadius: 0.75,
          fontSize: 11,
          lineHeight: 1.25,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          whiteSpace: "pre",
          overflow: "hidden",
          textOverflow: "clip",
          boxShadow: 3,
        }}
      >
        <Box sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          component: {state.name}
        </Box>
        <Box sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          type: {state.type}
        </Box>
        <Box sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          action: {state.action || "view"}
        </Box>
      </Box>
    </Box>,
    document.body,
  );
}
